import { NoStockLocationNotice } from '../../app/no-stock-location-notice.js';
import { useCollectionView } from '../../app/use-collection-view.js';
import {
  Button,
  DateRangePicker,
  IconButton,
  Modal,
  Select,
  StatusText,
  formatCivilRange,
  todayCivilDate,
  useIsWideViewport,
  useSnackbar,
  type StatusTone,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, PackageMinus, Settings2 } from 'lucide-react';
import { useState } from 'react';
import {
  getApiError,
  materialUsageApi,
  type MaterialUsageDetail,
  type MaterialUsageLine,
  type MaterialUsageState,
  type MaterialUsageSummary,
} from '../../lib/api.js';
import { factTimeZone, formatDateTime } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import type { MaterialUsageView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { unitLabels } from './units.js';
import { RegisteredLines, ReverseDialog } from './usage-lines.js';
import { PageHeader } from '../../app/page-title.js';

export interface MaterialUsageCapabilities {
  canReverse: boolean;
  canManageSettings: boolean;
}

const stateLabels: Record<MaterialUsageState, { label: string; tone: StatusTone }> = {
  NO_PLAN: { label: 'Sem previsto', tone: 'progress' },
  PENDING: { label: 'Pendente', tone: 'neutral' },
  MATCHED: { label: 'Conforme o previsto', tone: 'positive' },
  DIVERGENT: { label: 'Divergente', tone: 'warning' },
};

const usageKey = ['company', 'material-usage'] as const;

/// A conferência do consumo (ADR 0030, decisão 12): o registro acontece no fechamento do
/// atendimento; aqui ficam o comparativo, o histórico e o estorno.
export function CompanyMaterialUsage({
  capabilities,
  consolidated = false,
  timeZone,
  view,
  onView,
  onOpenClosing,
}: {
  capabilities: MaterialUsageCapabilities;
  /// Visão de todas as unidades (ADR 0039): só consulta, com a unidade de cada atendimento.
  consolidated?: boolean;
  timeZone: string;
  view: MaterialUsageView;
  onView: ViewSetter<MaterialUsageView>;
  /// Abre o fechamento do atendimento, onde o material é registrado. Ausente, o atalho não aparece.
  onOpenClosing?: ((appointmentId: string) => void) | undefined;
}) {
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const [settingsOpen, setSettingsOpen] = useState(false);
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: [...usageKey, 'list', filters],
    queryFn: () => materialUsageApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const total = page.data?.total ?? 0;
  const chips =
    (view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : [];

  return (
    <section aria-labelledby="company-material-usage-title">
      <PageHeader
        trail="Operação / Estoque"
        id="company-material-usage-title"
        title="Consumo de materiais"
        description="O que saiu do estoque em cada atendimento, contra o previsto quando houver protocolo."
        actions={
          <>
            {capabilities.canManageSettings ? (
              <Button variant="outline" onClick={() => setSettingsOpen(true)}>
                <Settings2 className="size-4" />
                Local padrão
              </Button>
            ) : null}
          </>
        }
      />

      {consolidated ? (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-surface-border bg-surface-card p-4 text-sm text-content-secondary"
        >
          Visão consolidada: consumo de todas as suas unidades, só para consulta. Para estornar ou
          mudar o local padrão, escolha uma unidade no seletor acima.
        </p>
      ) : null}

      <FilterBar
        searchId="material-usage-search"
        searchLabel="Buscar atendimento"
        searchPlaceholder="Cliente, serviço ou profissional"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ from: null, to: null })}
        count={`${total} ${total === 1 ? 'atendimento' : 'atendimentos'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'atendimento' : 'atendimentos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Atendimento</span>
          <DateRangePicker
            label="Dias de atendimento"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando atendimentos" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={PackageMinus}
          title="Nenhum atendimento concluído ou com material"
          description="O material é registrado no fechamento do atendimento, a partir da Agenda."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Consumo de materiais por atendimento</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  <th className="px-5 py-3 text-xs font-semibold text-content-muted">
                    Atendimento
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold text-content-muted">Cliente</th>
                  {consolidated ? (
                    <th className="px-5 py-3 text-xs font-semibold text-content-muted">Unidade</th>
                  ) : null}
                  <th className="w-44 px-5 py-3 text-xs font-semibold text-content-muted">
                    Situação
                  </th>
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.items.map((item) => (
                  <tr key={item.appointment.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">
                        {formatDateTime(
                          item.appointment.startsAt,
                          factTimeZone(item.appointment, timeZone, consolidated),
                        )}
                      </strong>
                      <span className="mt-1 block truncate text-xs text-content-muted">
                        {describe(item)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {item.appointment.clientDisplayName}
                    </td>
                    {consolidated ? (
                      <td className="px-5 py-4 text-xs text-content-muted">
                        {item.appointment.businessUnitName}
                      </td>
                    ) : null}
                    <td className="px-5 py-4">
                      <StateBadge item={item} />
                    </td>
                    <td className="px-3 py-4 text-right">
                      <IconButton
                        label="Ver consumo"
                        icon={<PackageMinus className="size-4" />}
                        onClick={() => onView({ appointment: item.appointment.id })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <ul className="divide-y divide-surface-border">
              {page.data?.items.map((item) => (
                <li key={item.appointment.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <strong className="block truncate text-sm">
                        {item.appointment.clientDisplayName}
                      </strong>
                      <span className="mt-1 block truncate text-xs text-content-muted">
                        {formatDateTime(
                          item.appointment.startsAt,
                          factTimeZone(item.appointment, timeZone, consolidated),
                        )}{' '}
                        · {describe(item)}
                      </span>
                      {consolidated ? (
                        <span className="mt-1 block truncate text-xs text-content-faint">
                          {item.appointment.businessUnitName}
                        </span>
                      ) : null}
                      <div className="mt-2">
                        <StateBadge item={item} />
                      </div>
                    </div>
                    <IconButton
                      label="Ver consumo"
                      icon={<PackageMinus className="size-4" />}
                      onClick={() => onView({ appointment: item.appointment.id })}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'atendimento' : 'atendimentos')}
          />
        </div>
      )}

      {view.appointment ? (
        <UsageDialog
          appointmentId={view.appointment}
          capabilities={capabilities}
          timeZone={timeZone}
          consolidated={consolidated}
          onOpenChange={() => onView({ appointment: undefined })}
          onOpenClosing={onOpenClosing}
        />
      ) : null}
      {settingsOpen ? <SettingsDialog onOpenChange={() => setSettingsOpen(false)} /> : null}
    </section>
  );
}

function describe(item: MaterialUsageSummary): string {
  return `${item.appointment.serviceDisplayName} · ${
    item.application?.status === 'APPLIED'
      ? item.application.protocolDisplayName
      : 'sem protocolo aplicado'
  }`;
}

function StateBadge({ item }: { item: MaterialUsageSummary }) {
  const state = stateLabels[item.state];
  return (
    <>
      <StatusText tone={state.tone} className="text-sm">
        {state.label}
      </StatusText>
      <span className="mt-1 block text-xs text-content-faint">
        {item.lineCount === 0
          ? 'nenhuma linha'
          : `${String(item.lineCount)} ${item.lineCount === 1 ? 'linha' : 'linhas'}`}
      </span>
    </>
  );
}

/// O consumo de um atendimento: o comparativo, o que foi registrado e o estorno. Registrar é no
/// fechamento, e o atalho leva até ele.
function UsageDialog({
  appointmentId,
  capabilities,
  timeZone: viewTimeZone,
  consolidated,
  onOpenChange,
  onOpenClosing,
}: {
  appointmentId: string;
  capabilities: MaterialUsageCapabilities;
  timeZone: string;
  consolidated: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenClosing?: ((appointmentId: string) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [reverseTarget, setReverseTarget] = useState<MaterialUsageLine>();
  const detail = useQuery({
    queryKey: [...usageKey, 'detail', appointmentId],
    queryFn: () => materialUsageApi.get(appointmentId),
    retry: false,
  });
  const data = detail.data;
  // O atendimento e as linhas dele se leem no fuso da unidade onde aconteceram (ADR 0044).
  const timeZone = data ? factTimeZone(data.appointment, viewTimeZone, consolidated) : viewTimeZone;

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Consumo do atendimento"
      description={
        data
          ? `${data.appointment.clientDisplayName} · ${formatDateTime(data.appointment.startsAt, timeZone)}${
              data.application?.status === 'APPLIED'
                ? ` · ${data.application.protocolDisplayName}`
                : ''
            }`
          : 'Carregando o atendimento.'
      }
      {...(onOpenClosing
        ? {
            footer: (
              <Button variant="outline" onClick={() => onOpenClosing(appointmentId)}>
                <ClipboardCheck className="size-4" />
                Abrir fechamento
              </Button>
            ),
          }
        : {})}
    >
      {detail.isLoading ? (
        <LoadingCard label="Carregando consumo" />
      ) : detail.error || !data ? (
        <ErrorCard message={getApiError(detail.error)} />
      ) : (
        <div className="grid gap-5">
          <Comparison detail={data} />
          <RegisteredLines
            lines={data.lines}
            canReverse={capabilities.canReverse}
            timeZone={timeZone}
            onReverse={setReverseTarget}
          />
          <p className="text-xs text-content-muted">
            Registrar material é no fechamento do atendimento. Aqui ficam o comparativo, o histórico
            e o estorno.
          </p>
        </div>
      )}
      {reverseTarget ? (
        <ReverseDialog
          line={reverseTarget}
          onOpenChange={() => setReverseTarget(undefined)}
          onReversed={async () => {
            await queryClient.invalidateQueries({ queryKey: usageKey });
            setReverseTarget(undefined);
            snackbar.success('Consumo estornado e devolvido ao estoque.');
          }}
        />
      ) : null}
    </Modal>
  );
}

function Comparison({ detail }: { detail: MaterialUsageDetail }) {
  if (detail.materials.length === 0) {
    return <p className="text-sm text-content-muted">Nenhum material registrado ainda.</p>;
  }
  if (detail.state === 'NO_PLAN') {
    return (
      <p className="text-sm text-content-muted">
        Sem protocolo aplicado neste atendimento: não há previsto para comparar. Abaixo, só o que
        saiu do estoque.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-surface-border">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">Previsto contra usado</caption>
        <thead>
          <tr className="border-b border-surface-border text-xs text-content-muted">
            <th className="px-4 py-2 font-semibold">Produto</th>
            <th className="px-4 py-2 text-right font-semibold">Previsto</th>
            <th className="px-4 py-2 text-right font-semibold">Usado</th>
            <th className="px-4 py-2 text-right font-semibold">Diferença</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-border">
          {detail.materials.map((row) => (
            <tr key={row.productId}>
              <td className="px-4 py-2">{row.productDisplayName}</td>
              <td className="px-4 py-2 text-right text-content-muted">
                {row.plannedQuantity} {unitLabels[row.baseUnit]}
              </td>
              <td className="px-4 py-2 text-right text-content-muted">
                {row.usedQuantity} {unitLabels[row.baseUnit]}
              </td>
              <td
                className={`px-4 py-2 text-right ${row.differenceQuantity === '0' ? 'text-content-muted' : 'font-semibold'}`}
              >
                {row.differenceQuantity === '0'
                  ? '—'
                  : `${row.differenceQuantity.startsWith('-') ? '' : '+'}${row.differenceQuantity} ${unitLabels[row.baseUnit]}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/// O local padrão pertence a quem consome, não ao cadastro do Estoque (ADR 0029, decisão 12).
function SettingsDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [requestError, setRequestError] = useState<string>();
  const [selected, setSelected] = useState<string>();
  const settings = useQuery({
    queryKey: [...usageKey, 'settings'],
    queryFn: () => materialUsageApi.getSettings(),
    retry: false,
  });
  const locations = useQuery({
    queryKey: [...usageKey, 'locations'],
    queryFn: () => materialUsageApi.listLocations(),
    retry: false,
  });
  const value = selected ?? settings.data?.defaultLocationId ?? '';

  async function submit() {
    setRequestError(undefined);
    try {
      await materialUsageApi.saveSettings(value ? value : null);
      await queryClient.invalidateQueries({ queryKey: usageKey });
      onOpenChange(false);
      snackbar.success('Local padrão de consumo atualizado.');
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Local padrão de consumo"
      description="De onde o material sai por padrão nos atendimentos. Continua ajustável por linha."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()}>Salvar</Button>
        </>
      }
    >
      {locations.isLoading || settings.isLoading ? (
        <LoadingCard label="Carregando locais" />
      ) : (
        <div className="grid gap-3">
          {locations.data?.length === 0 ? <NoStockLocationNotice /> : null}
          <Select
            label="Local"
            value={value}
            options={[
              { value: '', label: 'Sem local padrão' },
              ...(locations.data ?? []).map((location) => ({
                value: location.id,
                label: location.displayName,
              })),
            ]}
            onValueChange={setSelected}
          />
          {requestError ? <RequestAlert message={requestError} /> : null}
        </div>
      )}
    </Modal>
  );
}
